import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Video, Sparkles, CheckCircle2, ArrowRight, Users, Award, Loader2, TrendingUp, Briefcase, Target, Zap, PlayCircle, FileText, MessageCircle, Rocket, ShieldCheck, Laptop, Wifi, Notebook, Coffee, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import WorkshopEnrollModal from './WorkshopEnrollModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/workshops`;

const UpcomingWorkshopsSection = ({ variant = 'dark', cls, heading, subheading, eyebrow }) => {
  const [workshops, setWorkshops] = useState([]);
  const [enrollments, setEnrollments] = useState([]); // map: workshop_id -> enrollment
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const loadEnrollments = React.useCallback(async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('practiq_portal_token') : '';
    if (!token) { setEnrollments([]); return; }
    try {
      const r = await fetch(`${API}/my-enrollments`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { setEnrollments([]); return; }
      const data = await r.json();
      setEnrollments(data.enrollments || []);
    } catch {
      setEnrollments([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/upcoming`);
        const data = await r.json();
        if (!cancelled) setWorkshops(data.workshops || []);
      } catch {
        if (!cancelled) setWorkshops([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    loadEnrollments();
    return () => { cancelled = true; };
  }, [loadEnrollments]);

  const enrollmentByWorkshop = React.useMemo(() => {
    const map = {};
    for (const e of enrollments) map[e.workshop_id] = e;
    return map;
  }, [enrollments]);

  const isDark = variant === 'dark';

  if (loading) {
    return (
      <div className={`w-full flex items-center justify-center py-12 ${isDark ? 'text-gray-500' : ''}`}>
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!workshops.length) return null;

  // ---- HOMEPAGE variant (dark, hero-style) ----
  if (isDark) {
    return (
      <section id="upcoming-workshops" className="w-full bg-gradient-to-b from-[#0A0F1C] via-[#0B1120] to-[#080C16] py-20 border-t border-white/5 relative overflow-hidden">
        {/* Background flourishes */}
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.08) 1px, transparent 0)', backgroundSize: '48px 48px' }} />

        <div className="w-full px-6 lg:px-16 xl:px-24 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/30 mb-5 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
              <span className="text-xs text-teal-300 font-medium tracking-wider uppercase">{eyebrow || 'Live Cohort · Limited Seats'}</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              {heading || (
                <>
                  Master Practical GST Filing <br className="hidden md:block" />
                  <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">with Live Portal Simulations</span>
                </>
              )}
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed mb-3">
              {subheading || 'A step-by-step, hands-on masterclass — practice on the exact same portal flow, get your doubts cleared live, and walk away with a certificate that stands out on your resume.'}
            </p>
            <p className="text-sm text-teal-300/80 italic tracking-wide">
              PractiQ — Where Practice Meets Quality.
            </p>
          </div>

          <div className={`grid ${workshops.length === 1 ? 'grid-cols-1 max-w-6xl mx-auto' : 'md:grid-cols-2'} gap-8`}>
            {workshops.map((w) => (
              <WorkshopCardHero
                key={w.id}
                workshop={w}
                enrollment={enrollmentByWorkshop[w.id]}
                onEnroll={() => setSelectedId(w.id)}
              />
            ))}
          </div>

          {/* Testimonials — social proof */}
          <TestimonialsStrip />

          {/* FAQs — value clarification */}
          <FaqStrip />
        </div>

        <WorkshopEnrollModal
          isOpen={!!selectedId}
          workshopId={selectedId}
          onClose={() => setSelectedId(null)}
          onEnrolled={loadEnrollments}
        />
      </section>
    );
  }

  // ---- DASHBOARD variant (themed via cls) ----
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-teal-400" />
            {heading || 'Upcoming Live Workshops'}
          </h3>
          <p className={`text-xs mt-0.5 ${cls?.mutedText || 'text-gray-500'}`}>
            {subheading || 'Hands-on masterclass with Q&A · Certificate included'}
          </p>
        </div>
        <Badge className="bg-teal-500/15 text-teal-400 border-teal-500/30 text-[11px]">Live</Badge>
      </div>

      <div className="grid grid-cols-1 gap-4" data-testid="dashboard-workshops">
        {workshops.map((w) => (
          <WorkshopCardThemed
            key={w.id}
            workshop={w}
            enrollment={enrollmentByWorkshop[w.id]}
            cls={cls}
            onEnroll={() => setSelectedId(w.id)}
          />
        ))}
      </div>

      <WorkshopEnrollModal
        isOpen={!!selectedId}
        workshopId={selectedId}
        onClose={() => setSelectedId(null)}
        onEnrolled={loadEnrollments}
      />
    </div>
  );
};

/* ---------------- Homepage HERO card (big & premium) ---------------- */
const WorkshopCardHero = ({ workshop, enrollment, onEnroll }) => {
  // Curated "you will get" pointers — from workshop.perks/highlights, plus static career benefits
  const whatYouGet = [
    { icon: PlayCircle, text: 'Live hands-on walkthrough on the real GST portal simulator' },
    { icon: MessageCircle, text: 'Live Q&A with the instructor — ask anything, no doubts left behind' },
    { icon: Award, text: 'Certificate of Participation from PractiQ (great for your resume & LinkedIn)' },
    { icon: FileText, text: 'Downloadable reference PDF + session recording for 7 days' },
  ];

  const careerBenefits = [
    { icon: Briefcase, title: 'Job-Ready Skill', body: 'GST Registration is the #1 skill every CA firm, startup, and MSME needs today.' },
    { icon: Target, title: 'Client Confidence', body: 'Handle your first client independently without depending on seniors.' },
    { icon: TrendingUp, title: 'Earning Potential', body: 'Freelance GST professionals charge ₹1,500–₹5,000 per registration. Master it once.' },
    { icon: Rocket, title: 'Career Edge', body: 'Stand out in interviews with a live, verified certificate — not just a theory course.' },
  ];

  return (
    <div
      className="group relative bg-gradient-to-br from-[#111827] via-[#0F1729] to-[#0D1220] border border-white/10 hover:border-teal-500/50 rounded-3xl p-8 lg:p-10 transition-all overflow-hidden shadow-2xl shadow-black/40"
      data-testid={`workshop-card-${workshop.id}`}
    >
      {/* Corner accent glows */}
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-teal-500/15 rounded-full blur-3xl opacity-60 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl opacity-40 group-hover:opacity-70 transition-opacity duration-700 pointer-events-none" />

      <div className="relative z-10">
        {/* Badges row */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/40 text-xs px-3 py-1 hover:bg-rose-500/15">
            <span className="w-1.5 h-1.5 bg-rose-400 rounded-full mr-2 animate-pulse" />
            Live Cohort · Only {workshop.seats_left || 47} Seats Left
          </Badge>
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-xs px-3 py-1 hover:bg-emerald-500/15">
            <Award className="w-3 h-3 mr-1.5" /> Certificate Included
          </Badge>
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-xs px-3 py-1 hover:bg-amber-500/15">
            <Zap className="w-3 h-3 mr-1.5" /> Early Bird 60% OFF
          </Badge>
        </div>

        {/* Countdown */}
        <CountdownStrip targetIso={workshop.date_iso} />

        {/* Title */}
        <h3 className="text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight">
          {workshop.title}
        </h3>
        <p className="text-base text-gray-400 mb-6 leading-relaxed max-w-3xl">
          A 1.5–2 hour live hands-on masterclass on the <strong className="text-teal-300 font-semibold">PractiQ GST Portal Simulator</strong> covering the complete GST Registration workflow — PAN &amp; Aadhaar verification, TRN generation, business &amp; promoter details, principal place of business, HSN/SAC codes, bank verification, and ARN. Followed by a live Q&amp;A session with the instructor.
        </p>

        {/* Meta row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <MetaTile icon={Calendar} label="Date" value="23 Aug 2026" color="text-teal-400" />
          <MetaTile icon={Clock} label="Time" value="11:00 AM IST" color="text-cyan-400" />
          <MetaTile icon={Video} label="Platform" value="Zoom (Live)" color="text-emerald-400" />
          <MetaTile icon={Users} label="Instructor" value="CA Shivangi Mittal" color="text-amber-400" />
        </div>

        {/* Two-column deep content */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 mb-8">
          {/* What You'll Get */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-white">What You&apos;ll Get</h4>
            </div>
            <ul className="space-y-3">
              {whatYouGet.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <item.icon className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-300 leading-relaxed">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Why This Matters for Your Career */}
          <div className="bg-gradient-to-br from-amber-500/10 to-rose-500/5 border border-amber-500/20 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center">
                <Rocket className="w-4 h-4 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-white">Why This Matters for Your Career</h4>
            </div>
            <div className="space-y-4">
              {careerBenefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <b.icon className="w-4 h-4 text-amber-300" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white mb-0.5">{b.title}</p>
                    <p className="text-xs text-gray-400 leading-relaxed">{b.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ideal For (audience) */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 mb-6">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-3 font-semibold">Perfect for</p>
          <div className="flex flex-wrap gap-2">
            {['CA Students & Articles', 'Junior Accountants', 'MSME Owners', 'Freelance Tax Consultants', 'Commerce Graduates', 'Finance Professionals'].map((tag) => (
              <span key={tag} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Prerequisites — what to prepare before enrolling */}
        <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/5 border border-cyan-500/20 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <h4 className="text-lg font-semibold text-white">Before You Enroll — Prerequisites</h4>
          </div>
          <p className="text-xs text-gray-400 mb-4 pl-10">
            This is a <span className="text-cyan-300 font-medium">hands-on live session</span>. To practice along with the instructor and actually learn GST filing yourself, please keep these ready:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <PrereqTile icon={Laptop} title="Laptop / Desktop" body="Mobile won't work for the GST portal practice." required />
            <PrereqTile icon={Wifi} title="Stable Wi-Fi" body="At least 5 Mbps for Zoom + portal simultaneously." required />
            <PrereqTile icon={Notebook} title="Notepad & Pen" body="Jot down HSN codes, ARN, and important tips." />
            <PrereqTile icon={Coffee} title="90 Minutes Free" body="Kindly attend without interruptions." />
          </div>
          <div className="mt-4 flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <Zap className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-100 leading-relaxed">
              Attending only from a mobile? You can still watch, but you won&apos;t be able to practice on the GST portal live with us.
            </p>
          </div>
        </div>

        {/* Price + CTA row  OR  Enrolled status */}
        {enrollment ? (
          <EnrolledStatusHero enrollment={enrollment} />
        ) : (
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-6 border-t border-white/10">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-widest mb-1 font-semibold">Enroll Now</p>
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent">₹{workshop.price}</span>
                <span className="text-xl text-gray-500 line-through">₹499</span>
                <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40 text-[11px] hover:bg-rose-500/20">
                  Save ₹300
                </Badge>
              </div>
              <p className="text-sm text-gray-400 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                Instant Zoom Confirmation &amp; Bonus Resources sent on WhatsApp / Email.
              </p>
            </div>

            <div className="flex flex-col items-stretch md:items-end gap-2 w-full md:w-auto">
              <Button
                onClick={onEnroll}
                size="lg"
                className="w-full md:w-auto h-14 px-8 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white text-base font-semibold shadow-xl shadow-teal-500/30 transition-transform hover:scale-[1.02]"
                data-testid={`workshop-enroll-${workshop.id}`}
              >
                Enroll Now — ₹{workshop.price}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-[11px] text-gray-500 text-center md:text-right">UPI · GPay · PhonePe · Paytm · BHIM</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MetaTile = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-3.5">
    <Icon className={`w-4 h-4 ${color} mb-2`} />
    <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-0.5">{label}</p>
    <p className="text-sm font-semibold text-white">{value}</p>
  </div>
);

const PrereqTile = ({ icon: Icon, title, body, required = false }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3.5 relative">
    {required && (
      <span className="absolute top-2 right-2 text-[9px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40 px-1.5 py-0.5 rounded uppercase tracking-wider">
        Must
      </span>
    )}
    <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-2.5">
      <Icon className="w-4 h-4 text-cyan-300" />
    </div>
    <p className="text-sm font-semibold text-white mb-1">{title}</p>
    <p className="text-[11px] text-gray-400 leading-relaxed">{body}</p>
  </div>
);

/* ---------------- Countdown Strip ---------------- */
const CountdownCell = ({ v, label }) => (
  <div className="flex flex-col items-center min-w-[54px]">
    <span className="text-xl md:text-2xl font-bold text-white leading-none tabular-nums">{String(v).padStart(2, '0')}</span>
    <span className="text-[9px] text-gray-500 uppercase tracking-wider mt-0.5">{label}</span>
  </div>
);

const CountdownStrip = ({ targetIso }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000); // update every minute
    return () => clearInterval(id);
  }, []);

  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  const diff = Math.max(0, target - now);

  if (diff <= 0) {
    return (
      <div className="mb-5 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30">
        <Zap className="w-4 h-4 text-rose-300" />
        <span className="text-sm text-rose-200 font-medium">Enrollment closed · This workshop has started</span>
      </div>
    );
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);

  return (
    <div className="mb-6 flex items-center gap-3 md:gap-4 px-4 py-3 rounded-xl bg-gradient-to-r from-rose-500/10 via-amber-500/10 to-rose-500/10 border border-rose-500/25 flex-wrap">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center">
          <Clock className="w-4 h-4 text-rose-300" />
        </div>
        <p className="text-xs text-rose-200 uppercase tracking-widest font-semibold">Enrollment closes in</p>
      </div>
      <div className="flex items-center gap-2 md:gap-3 ml-auto">
        <CountdownCell v={days} label="Days" />
        <span className="text-white/30 text-xl">:</span>
        <CountdownCell v={hours} label="Hours" />
        <span className="text-white/30 text-xl">:</span>
        <CountdownCell v={mins} label="Mins" />
      </div>
    </div>
  );
};

/* ---------------- Testimonials Strip ---------------- */
const TESTIMONIALS = [
  {
    name: 'Ananya Verma',
    role: 'CA Final Student · Delhi',
    initials: 'AV',
    quote: 'The live portal walkthrough was gold. I finally understood the flow that theory couldn\u2019t teach me.',
    accent: 'from-teal-500 to-cyan-500',
  },
  {
    name: 'Rohit Bansal',
    role: 'Junior Accountant · Mumbai',
    initials: 'RB',
    quote: 'Handled my first client GST registration solo the very next week. Worth 10x the price.',
    accent: 'from-amber-500 to-orange-500',
  },
  {
    name: 'Priya Sharma',
    role: 'MSME Owner · Jaipur',
    initials: 'PS',
    quote: 'CA Shivangi ma\u2019am answered every doubt live. Never felt like a recorded course — this is real teaching.',
    accent: 'from-purple-500 to-pink-500',
  },
];

const TestimonialsStrip = () => (
  <div className="mt-16">
    <div className="text-center mb-8">
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-3">
        <Users className="w-4 h-4 text-emerald-400" />
        <span className="text-xs text-emerald-300 font-medium tracking-wider uppercase">Trusted by 100+ CA aspirants &amp; finance students</span>
      </div>
      <h3 className="text-2xl lg:text-3xl font-bold text-white">
        What students are saying
      </h3>
    </div>

    <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
      {TESTIMONIALS.map((t, i) => (
        <div
          key={i}
          className="bg-white/[0.03] border border-white/10 hover:border-white/20 rounded-2xl p-6 transition-all"
          data-testid={`testimonial-${i}`}
        >
          <div className="flex items-center gap-1 mb-3">
            {[...Array(5)].map((_, k) => (
              <svg key={k} className="w-4 h-4 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.966a1 1 0 00.95.69h4.169c.969 0 1.371 1.24.588 1.81l-3.374 2.452a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118L10 13.187l-3.374 2.452c-.784.57-1.838-.196-1.539-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.635 9.385c-.783-.57-.38-1.81.588-1.81h4.17a1 1 0 00.95-.69l1.286-3.966z" />
              </svg>
            ))}
          </div>
          <p className="text-sm text-gray-300 leading-relaxed mb-5 italic">
            &ldquo;{t.quote}&rdquo;
          </p>
          <div className="flex items-center gap-3 pt-4 border-t border-white/5">
            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.accent} flex items-center justify-center text-white text-sm font-bold`}>
              {t.initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{t.name}</p>
              <p className="text-[11px] text-gray-500 truncate">{t.role}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ---------------- Dashboard themed card (light-theme aware) ---------------- */
const WorkshopCardThemed = ({ workshop, enrollment, cls, onEnroll }) => (
  <div
    className={`${cls?.card || 'bg-white/5 border-white/10'} border rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4`}
    data-testid={`workshop-card-${workshop.id}`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Badge className="bg-rose-500/15 text-rose-400 border-rose-500/30 text-[10px]">
          <span className="w-1.5 h-1.5 bg-rose-400 rounded-full mr-1 animate-pulse" />
          Live
        </Badge>
        <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">
          <Award className="w-3 h-3 mr-1" /> Certificate
        </Badge>
        <span className={`text-[11px] ${cls?.mutedText || 'text-gray-500'}`}>· {workshop.duration}</span>
      </div>
      <h4 className="text-base font-semibold mb-1">{workshop.title}</h4>
      <div className={`flex items-center gap-3 text-xs ${cls?.mutedText || 'text-gray-500'} mb-2 flex-wrap`}>
        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {workshop.date_label}</span>
        <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> Zoom</span>
        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {workshop.instructor}</span>
      </div>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      {enrollment ? (
        <EnrolledStatusCompact enrollment={enrollment} cls={cls} />
      ) : (
        <>
          <div className="text-right">
            <p className={`text-[10px] uppercase tracking-wider ${cls?.mutedText || 'text-gray-500'}`}>Price</p>
            <p className="text-xl font-bold">₹{workshop.price}</p>
          </div>
          <Button
            onClick={onEnroll}
            className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white"
            data-testid={`workshop-enroll-${workshop.id}`}
          >
            Enroll <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </>
      )}
    </div>
  </div>
);

/* ---------------- Enrolled status blocks ---------------- */
const statusCopy = (status) => {
  switch (status) {
    case 'confirmed':
      return {
        label: 'Payment Confirmed',
        headline: 'You are enrolled ✓',
        body: 'Your Zoom link has been shared on your email. See you in the session!',
        tone: 'emerald',
      };
    case 'rejected':
      return {
        label: 'Payment Not Verified',
        headline: 'Payment could not be verified',
        body: 'Please reach out to support@taxsetu.ai with your payment proof so we can help.',
        tone: 'rose',
      };
    default:
      return {
        label: 'Payment Received',
        headline: 'Payment received — verifying',
        body: 'Our team is verifying your payment. You\u2019ll get the Zoom join link on email &amp; WhatsApp within a few hours.',
        tone: 'amber',
      };
  }
};

const EnrolledStatusHero = ({ enrollment }) => {
  const s = statusCopy(enrollment.status);
  const toneStyles = {
    emerald: 'from-emerald-500/15 to-teal-500/5 border-emerald-500/30 text-emerald-200',
    amber:   'from-amber-500/15 to-orange-500/5 border-amber-500/30 text-amber-200',
    rose:    'from-rose-500/15 to-orange-500/5 border-rose-500/30 text-rose-200',
  }[s.tone];
  const iconTone = {
    emerald: 'text-emerald-400',
    amber:   'text-amber-400',
    rose:    'text-rose-400',
  }[s.tone];
  return (
    <div className={`rounded-2xl border p-5 bg-gradient-to-br ${toneStyles}`} data-testid="workshop-enrolled-status">
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0`}>
          <CheckCircle2 className={`w-5 h-5 ${iconTone}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] uppercase tracking-widest font-semibold ${iconTone} mb-1`}>{s.label}</p>
          <h4 className="text-lg font-semibold text-white mb-1">{s.headline}</h4>
          <p className="text-sm text-gray-300 leading-relaxed">
            {s.body}
          </p>
          {enrollment.status === 'confirmed' && enrollment.join_link ? (
            <a
              href={enrollment.join_link}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white text-sm font-medium transition-transform hover:scale-[1.02]"
              data-testid="workshop-join-link"
            >
              <Video className="w-4 h-4" />
              Join Live Session
            </a>
          ) : (
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
              <Mail className="w-3.5 h-3.5" />
              <span>Zoom link will be emailed to <span className="text-white font-medium">{enrollment.email}</span></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EnrolledStatusCompact = ({ enrollment }) => {
  const s = statusCopy(enrollment.status);
  const badgeStyle = {
    emerald: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    amber:   'bg-amber-500/15 text-amber-400 border-amber-500/30',
    rose:    'bg-rose-500/15 text-rose-400 border-rose-500/30',
  }[s.tone];
  return (
    <div className="flex flex-col items-end gap-1.5" data-testid="workshop-enrolled-status">
      <Badge className={`${badgeStyle} text-[11px] whitespace-nowrap`}>
        <CheckCircle2 className="w-3 h-3 mr-1" /> {s.label}
      </Badge>
      {enrollment.status === 'confirmed' && enrollment.join_link ? (
        <a
          href={enrollment.join_link}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-teal-400 hover:underline flex items-center gap-1"
          data-testid="workshop-join-link"
        >
          <Video className="w-3.5 h-3.5" /> Join Zoom →
        </a>
      ) : (
        <p className="text-[11px] text-gray-500 max-w-[200px] text-right leading-tight">Zoom link will be emailed shortly</p>
      )}
    </div>
  );
};

/* ---------------- FAQ Strip ---------------- */
const FAQS = [
  {
    q: 'The GST Registration Simulator is free — why should I pay ₹199 for this workshop?',
    a: 'The free simulator lets you practice by yourself, at your own pace. This workshop is very different — you get **live guidance from CA Shivangi Mittal**, real-time doubt clarification, insider tips no simulator can teach you, a **PractiQ Certificate of Participation** for your resume, and a **7-day session recording**. In short — the simulator teaches you the flow, the workshop teaches you the wisdom behind each decision. You are paying for the instructor, not the tool.',
  },
  {
    q: 'What exactly makes the workshop worth ₹199?',
    a: 'A 1.5–2 hour live session with a Chartered Accountant, an interactive Q&A where every doubt is answered, downloadable reference PDF, session recording for 7 days, and a shareable Certificate of Participation. Comparable CA-led workshops usually cost ₹1,500–₹3,000. This is an early-bird price for our first cohort.',
  },
  {
    q: 'Can I use the certificate on my resume / LinkedIn?',
    a: 'Yes! The Certificate of Participation from PractiQ is a great addition to your CV, LinkedIn, and CA article-ship applications. It shows recruiters and firms that you have completed hands-on, portal-level GST Registration training.',
  },
  {
    q: 'What if I miss the live session?',
    a: 'No worries — the full session recording is shared with all enrolled participants for 7 days. You can watch it anytime, pause, rewind, and practice along.',
  },
  {
    q: 'Do I really need a laptop? Can I attend from mobile?',
    a: 'You can watch the session on mobile, but you won\u2019t be able to practice on the GST portal alongside the instructor. To actually learn by doing (which is the whole point), please join on a laptop or desktop with a stable internet connection.',
  },
  {
    q: 'I paid via UPI — when will I get the Zoom link?',
    a: 'Our team manually verifies every payment (usually within a few hours). Once verified, we email the Zoom join link to the address you enrolled with. You will also see the status update inside your PractiQ dashboard under "My Workshops".',
  },
  {
    q: 'Is there a refund if I change my mind?',
    a: 'Since your seat is blocked the moment you enroll (and we have limited seats), we don\u2019t process refunds. But if you can\u2019t attend live, you get the full recording — so you never really miss out.',
  },
  {
    q: 'Who is this workshop ideal for?',
    a: 'CA students & article assistants, junior accountants, MSME/startup owners handling their own GST, freelance tax consultants, commerce graduates preparing for jobs, and anyone who wants confidence to handle GST Registration end-to-end.',
  },
];

const renderInline = (text) => {
  // Bold **word** support without a full markdown parser
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="text-teal-300 font-semibold">{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
};

const FaqStrip = () => {
  const [openIndex, setOpenIndex] = useState(0); // First FAQ open by default

  return (
    <div className="mt-16 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/25 mb-3">
          <MessageCircle className="w-4 h-4 text-cyan-400" />
          <span className="text-xs text-cyan-300 font-medium tracking-wider uppercase">Frequently Asked Questions</span>
        </div>
        <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">
          Still have a doubt? We&apos;ve got you.
        </h3>
        <p className="text-sm text-gray-400">
          Common questions from CA students &amp; finance folks — answered honestly.
        </p>
      </div>

      <div className="space-y-3">
        {FAQS.map((f, i) => {
          const isOpen = openIndex === i;
          return (
            <div
              key={i}
              className={`bg-white/[0.03] border rounded-2xl transition-colors ${isOpen ? 'border-teal-500/40' : 'border-white/10 hover:border-white/20'}`}
              data-testid={`faq-${i}`}
            >
              <button
                onClick={() => setOpenIndex(isOpen ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="text-sm md:text-base font-medium text-white">{f.q}</span>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isOpen ? 'bg-teal-500/20 rotate-45' : 'bg-white/5'}`}>
                  <span className="text-teal-300 text-lg leading-none">+</span>
                </span>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 -mt-1 text-sm text-gray-300 leading-relaxed border-t border-white/5 pt-3">
                  {renderInline(f.a)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-center bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
        <p className="text-sm text-gray-400 mb-2">Still confused? Write to us at</p>
        <a href="mailto:support@taxsetu.ai" className="text-base text-teal-300 hover:text-teal-200 font-medium">
          support@taxsetu.ai
        </a>
      </div>
    </div>
  );
};

export default UpcomingWorkshopsSection;
